/**
 * WeChat API calls: OAuth authorize/callback, bind/unbind, JS-SDK signature.
 */

import { callWithRefresh } from "./auth-flow";
import { apiClient } from "./client";
import { endpoints } from "./endpoints";
import type {
  AuthResponse,
  JsapiSignatureIn,
  JsapiSignatureOut,
  UserOut,
  WechatAuthorizeOut,
  WechatBindIn,
  WechatCallbackIn,
} from "./types";

export const wechatApi = {
  /** GET /auth/wechat/authorize — returns OAuth2 redirect URL. */
  async authorize(): Promise<WechatAuthorizeOut> {
    return callWithRefresh<WechatAuthorizeOut>(endpoints.wechat.authorize, {
      method: "GET",
    });
  },

  /** POST /auth/wechat/callback — exchange code for JWT tokens. */
  async callback(payload: WechatCallbackIn): Promise<AuthResponse> {
    // callback does not go through callWithRefresh (no existing token to refresh)
    return apiClient<AuthResponse>(endpoints.wechat.callback, {
      method: "POST",
      body: payload,
    });
  },

  /** POST /auth/wechat/bind — bind WeChat to current user. */
  async bind(payload: WechatBindIn): Promise<UserOut> {
    return callWithRefresh<UserOut>(endpoints.wechat.bind, {
      method: "POST",
      body: payload,
    });
  },

  /** DELETE /auth/wechat/unbind — unbind WeChat from current user. */
  async unbind(): Promise<UserOut> {
    return callWithRefresh<UserOut>(endpoints.wechat.unbind, {
      method: "DELETE",
    });
  },

  /** POST /auth/wechat/jsapi-signature — JS-SDK signature for wx.config(). */
  async jsapiSignature(payload: JsapiSignatureIn): Promise<JsapiSignatureOut> {
    return callWithRefresh<JsapiSignatureOut>(endpoints.wechat.jsapiSignature, {
      method: "POST",
      body: payload,
    });
  },
};
